/*
  Forms for the structure of the ontology: item, family, sub-family, component kind, component, capability.
  An item asks the library, family, sub-family and main parent, and "drawn on which kinds of drawing?" (7.10).
*/

import type { ConnectionEnd, Id, Json, LimitOrigin } from "../../../api/types";
import { useApiQuery } from "../../../api/hooks";
import { ElementPicker, MultiElementPicker, NONE } from "../components/ElementPicker";
import { Check, Select, TextArea, TextInput } from "../components/fields";
import { childrenOfKind, mainParentCandidates, nodesOfKind } from "../lib/tree";
import { isWholeNumber } from "../lib/text";
import { DrawnOnPicker } from "./DrawnOnPicker";
import { bool, defineSpec, ids, opt, rec, str, type Errors, type FieldsProps } from "./types";

// ------------------------------------------------------------------------------------------------ item

type RangeKind = ConnectionEnd["range_kind"];

// `runId`: where on the line the end sits (a component kind of that line), only when the end names another Connection.
type EndValues = { rangeKind: RangeKind | ""; rangeId: string; runId: string; maxCount: string };

type ItemValues = {
  libraryId: string;
  familyId: string;
  subFamilyId: string;
  mainParentId: string;
  abstract: boolean;
  isConnection: boolean;
  capabilityIds: Id[];
  drawnOn: Id[];
  fromEnd: EndValues;
  toEnd: EndValues;
};

const emptyEnd = (): EndValues => ({ rangeKind: "", rangeId: "", runId: "", maxCount: "" });

function endFrom(value: unknown): EndValues {
  const end = rec(value);
  const kind = str(end.range_kind);
  return {
    rangeKind: (["item", "capability", "connection", "component_kind"] as const).find((k) => k === kind) ?? "",
    rangeId: str(end.range_id),
    runId: str(end.run_id),
    maxCount: end.max_count == null ? "" : String(end.max_count),
  };
}

function endContent(end: EndValues, base: unknown): Json {
  const before = rec(base);
  const origin: LimitOrigin = before.limit_origin === "dexpi" ? "dexpi" : "our_team";
  return {
    range_kind: end.rangeKind as RangeKind,
    range_id: end.rangeId,
    run_id: end.rangeKind === "connection" && end.runId ? end.runId : null,
    max_count: end.maxCount.trim() ? Number(end.maxCount) : null,
    limit_origin: origin,
  };
}

const RANGE_KIND_OPTIONS: Array<{ value: RangeKind; label: string }> = [
  { value: "item", label: "An item" },
  { value: "connection", label: "Another Connection" },
  { value: "component_kind", label: "A component kind" },
  { value: "capability", label: "Anything with a capability" },
];

function rangeElementKinds(kind: RangeKind | ""): Array<"item" | "capability" | "component_kind"> {
  switch (kind) {
    case "capability":
      return ["capability"];
    case "component_kind":
      return ["component_kind"];
    default:
      return ["item"];
  }
}

function EndFields({ title, end, onChange, errors, prefix }: { title: string; end: EndValues; onChange: (end: EndValues) => void; errors: Errors; prefix: string }) {
  return (
    <fieldset className="field field--group connection-end">
      <legend className="field__label">{title}</legend>
      <Select
        label="What it joins"
        required
        value={end.rangeKind}
        placeholder="Choose one"
        options={RANGE_KIND_OPTIONS}
        error={errors[`${prefix}Kind`]}
        onChange={(value) => onChange({ ...end, rangeKind: value as RangeKind | "", rangeId: "", runId: "" })}
      />
      {end.rangeKind && (
        <ElementPicker
          label="Which one"
          required
          kinds={rangeElementKinds(end.rangeKind)}
          value={end.rangeId}
          error={errors[`${prefix}Id`]}
          onChange={(id) => onChange({ ...end, rangeId: id })}
        />
      )}
      {end.rangeKind === "connection" && end.rangeId && (
        <ElementPicker
          label="Where on the line (optional)"
          hint="The kind of run of that line where this end sits. Leave it as it is when the place on the line does not matter."
          kinds={["component_kind"]}
          value={end.runId}
          placeholder="Anywhere on the line"
          onChange={(runId) => onChange({ ...end, runId })}
        />
      )}
      <TextInput
        label="How many may end here"
        hint="Leave empty for any number."
        value={end.maxCount}
        error={errors[`${prefix}Max`]}
        onChange={(value) => onChange({ ...end, maxCount: value })}
      />
    </fieldset>
  );
}

function validateEnd(end: EndValues, prefix: string, errors: Errors, word: string): void {
  if (!end.rangeKind) errors[`${prefix}Kind`] = `Say what the Connection ${word} at.`;
  else if (!end.rangeId) errors[`${prefix}Id`] = "Choose which one.";
  if (end.maxCount.trim() && !isWholeNumber(end.maxCount)) errors[`${prefix}Max`] = "Write a whole number of 1 or more, or leave it empty.";
}

function ItemFields({ values, set, errors, mode, elementId }: FieldsProps<ItemValues>) {
  const tree = useApiQuery("family_tree", {}, { staleTime: 15_000 });
  const libraries = nodesOfKind(tree.data, "library");
  const families = childrenOfKind(tree.data, values.libraryId, "family");
  const subFamilies = childrenOfKind(tree.data, values.familyId, "sub_family");
  const heading = values.subFamilyId && values.subFamilyId !== NONE ? values.subFamilyId : values.familyId;
  const parents = mainParentCandidates(tree.data, heading);
  const adding = mode === "add";

  return (
    <>
      {adding && (
        <fieldset className="field field--group">
          <legend className="field__label">Where it sits in the family tree</legend>
          <p className="field__hint muted">Library, family, sub-family and main parent. Answer each one, so the item is never lost in the tree.</p>
          {!tree.isPending && libraries.length === 0 && <p className="muted">No library is stored in this discipline yet.</p>}
          <Select
            label="Library"
            required
            value={values.libraryId}
            placeholder="Choose a library"
            options={libraries.map((node) => ({ value: node.id, label: node.label }))}
            error={errors.libraryId}
            onChange={(value) => set({ libraryId: value, familyId: "", subFamilyId: "", mainParentId: "" })}
          />
          <Select
            label="Family"
            required
            value={values.familyId}
            placeholder={values.libraryId ? "Choose a family" : "Choose a library first"}
            options={families.map((node) => ({ value: node.id, label: node.label }))}
            error={errors.familyId}
            disabled={!values.libraryId}
            onChange={(value) => set({ familyId: value, subFamilyId: "", mainParentId: "" })}
          />
          <Select
            label="Sub-family"
            required
            value={values.subFamilyId}
            placeholder={values.familyId ? "Choose a sub-family, or say there is none" : "Choose a family first"}
            options={[{ value: NONE, label: "None: it sits directly in the family" }, ...subFamilies.map((node) => ({ value: node.id, label: node.label }))]}
            error={errors.subFamilyId}
            disabled={!values.familyId}
            onChange={(value) => set({ subFamilyId: value, mainParentId: "" })}
          />
          <Select
            label="Main parent"
            required
            hint="What this item is a kind of. A top-level item has none."
            value={values.mainParentId}
            placeholder={values.familyId ? "Choose its main parent, or say there is none" : "Choose a family first"}
            options={[{ value: NONE, label: "None: it is a top-level item" }, ...parents.map((node) => ({ value: node.id, label: node.label }))]}
            error={errors.mainParentId}
            disabled={!values.familyId}
            onChange={(value) => set({ mainParentId: value })}
          />
        </fieldset>
      )}
      {!adding && (
        <ElementPicker
          label="Main parent"
          hint="What this item is a kind of. A top-level item has none."
          kinds={["item"]}
          value={values.mainParentId}
          noneLabel="None: it is a top-level item"
          exclude={elementId ? [elementId] : undefined}
          onChange={(id) => set({ mainParentId: id || NONE })}
        />
      )}
      <Check
        label="A parent item that is never drawn"
        hint="It holds the data every kind below it shares. It gets no symbol and cannot be picked for a real thing."
        checked={values.abstract}
        onChange={(abstract) => set({ abstract })}
      />
      <Check
        label="This item is a Connection"
        hint="A Connection joins two items at tie-in points, such as a pipe or a cable."
        checked={values.isConnection}
        onChange={(isConnection) => set({ isConnection })}
      />
      {values.isConnection && (
        <>
          <EndFields title="Where the Connection starts" end={values.fromEnd} errors={errors} prefix="from" onChange={(fromEnd) => set({ fromEnd })} />
          <EndFields title="Where the Connection finishes" end={values.toEnd} errors={errors} prefix="to" onChange={(toEnd) => set({ toEnd })} />
        </>
      )}
      <MultiElementPicker
        label="Capabilities it has"
        hint="Something it has or can do, shared with items from other branches."
        kinds={["capability"]}
        values={values.capabilityIds}
        onChange={(capabilityIds) => set({ capabilityIds })}
      />
      {adding && <DrawnOnPicker values={values.drawnOn} onChange={(drawnOn) => set({ drawnOn })} />}
    </>
  );
}

export const itemSpec = defineSpec<ItemValues>({
  kind: "item",
  title: "Item",
  blurb: "A complete thing that appears as one thing on a drawing, with all its components.",
  nameLabel: "Name of the item",
  definitionHint: "One or two plain sentences that say what it is.",
  empty: (draft) => ({
    libraryId: draft?.library_id ?? "",
    familyId: draft?.family_id ?? "",
    subFamilyId: draft?.sub_family_id ?? "",
    mainParentId: draft?.main_parent_id ?? "",
    abstract: draft?.abstract ?? false,
    isConnection: draft?.is_connection ?? false,
    capabilityIds: [],
    drawnOn: draft?.drawn_on ?? [],
    fromEnd: emptyEnd(),
    toEnd: emptyEnd(),
  }),
  fromElement: (row) => {
    const c = row.content;
    return {
      libraryId: "",
      familyId: "",
      subFamilyId: "",
      mainParentId: str(c.main_parent_id) || NONE,
      abstract: bool(c.abstract),
      isConnection: bool(c.is_connection),
      capabilityIds: ids(c.capability_ids),
      drawnOn: [],
      fromEnd: endFrom(c.from_end),
      toEnd: endFrom(c.to_end),
    };
  },
  validate: (v, mode) => {
    const errors: Errors = {};
    if (mode === "add") {
      if (!v.libraryId) errors.libraryId = "Choose the library.";
      if (!v.familyId) errors.familyId = "Choose the family.";
      if (!v.subFamilyId) errors.subFamilyId = "Choose a sub-family, or say there is none.";
      if (!v.mainParentId) errors.mainParentId = "Choose the main parent, or say it is a top-level item.";
    }
    if (v.isConnection) {
      validateEnd(v.fromEnd, "from", errors, "starts");
      validateEnd(v.toEnd, "to", errors, "finishes");
    }
    return errors;
  },
  toContent: (v, base) => {
    const heading = v.subFamilyId && v.subFamilyId !== NONE ? v.subFamilyId : v.familyId;
    return {
      ...base,
      main_parent_id: v.mainParentId && v.mainParentId !== NONE ? v.mainParentId : null,
      abstract: v.abstract,
      is_connection: v.isConnection,
      // An item names its lowest heading (profile section 3); an item being changed keeps the headings it has.
      subset_ids: base ? ids(base.subset_ids) : heading ? [heading] : [],
      capability_ids: v.capabilityIds,
      shared_field_ids: base ? ids(base.shared_field_ids) : [],
      from_end: v.isConnection ? endContent(v.fromEnd, base?.from_end) : null,
      to_end: v.isConnection ? endContent(v.toEnd, base?.to_end) : null,
      linkml_extra: base?.linkml_extra ?? {},
    };
  },
  drawnOn: (v) => v.drawnOn,
  Fields: ItemFields,
});

// ------------------------------------------------------------------------------------------------ family

type FamilyValues = { libraryId: string };

export const familySpec = defineSpec<FamilyValues>({
  kind: "family",
  title: "Family",
  blurb: "A heading inside a library that groups items. It holds no data.",
  nameLabel: "Name of the family",
  definitionHint: "What kind of items this family groups.",
  empty: (draft) => ({ libraryId: draft?.library_id ?? "" }),
  fromElement: (row) => ({ libraryId: str(row.content.parent_id) }),
  validate: (v): Errors => (v.libraryId ? {} : { libraryId: "Choose the library the family belongs to." }),
  toContent: (v, base) => ({ ...base, parent_id: v.libraryId }),
  Fields: ({ values, set, errors }) => (
    <ElementPicker label="Library" required kinds={["library"]} value={values.libraryId} error={errors.libraryId} onChange={(libraryId) => set({ libraryId })} />
  ),
});

// ------------------------------------------------------------------------------------------------ sub-family

type SubFamilyValues = { familyId: string };

export const subFamilySpec = defineSpec<SubFamilyValues>({
  kind: "sub_family",
  title: "Sub-family",
  blurb: "A heading inside a family that groups items. It is never drawn and holds no data, components or links.",
  nameLabel: "Name of the sub-family",
  definitionHint: "What kind of items this sub-family groups.",
  empty: (draft) => ({ familyId: draft?.family_id ?? "" }),
  fromElement: (row) => ({ familyId: str(row.content.parent_id) }),
  validate: (v): Errors => (v.familyId ? {} : { familyId: "Choose the family the sub-family belongs to." }),
  toContent: (v, base) => ({ ...base, parent_id: v.familyId }),
  Fields: ({ values, set, errors }) => (
    <ElementPicker label="Family" required kinds={["family"]} value={values.familyId} error={errors.familyId} onChange={(familyId) => set({ familyId })} />
  ),
});

// ------------------------------------------------------------------------------------------------ component kind

type ComponentKindValues = {
  libraryIds: Id[];
  mainParentId: string;
  capabilityIds: Id[];
  tieInNote: string;
  noTieIn: boolean;
  drawnOn: Id[];
};

function ComponentKindFields({ values, set, errors, mode, elementId }: FieldsProps<ComponentKindValues>) {
  return (
    <>
      <MultiElementPicker
        label="Libraries it belongs to"
        hint="A component kind names its libraries only, not a family."
        kinds={["library"]}
        values={values.libraryIds}
        onChange={(libraryIds) => set({ libraryIds })}
      />
      <ElementPicker
        label="Kind of (main parent)"
        hint="Another component kind this one is a kind of. Leave it as it is when it stands alone."
        kinds={["component_kind"]}
        value={values.mainParentId}
        placeholder="None: it stands alone"
        exclude={elementId ? [elementId] : undefined}
        onChange={(id) => set({ mainParentId: id })}
      />
      <fieldset className="field field--group">
        <legend className="field__label">Tie-in note</legend>
        <p className="field__hint muted">
          Say what may end at a component of this kind ("a line may end here"). Every component of the kind shows the mark. A tie-in point is not an element of its own.
        </p>
        <TextArea
          label="The note"
          value={values.tieInNote}
          rows={2}
          error={errors.tieInNote}
          disabled={values.noTieIn}
          onChange={(tieInNote) => set({ tieInNote })}
        />
        <Check label="Nothing may end at a component of this kind" checked={values.noTieIn} onChange={(noTieIn) => set({ noTieIn })} />
      </fieldset>
      <MultiElementPicker
        label="Capabilities it has"
        kinds={["capability"]}
        values={values.capabilityIds}
        onChange={(capabilityIds) => set({ capabilityIds })}
      />
      {mode === "add" && <DrawnOnPicker values={values.drawnOn} onChange={(drawnOn) => set({ drawnOn })} />}
    </>
  );
}

export const componentKindSpec = defineSpec<ComponentKindValues>({
  kind: "component_kind",
  title: "Component kind",
  blurb: "A reusable definition of a part that several items use, with its tie-in note.",
  nameLabel: "Name of the component kind",
  definitionHint: "What this kind of component is.",
  empty: (draft) => ({
    libraryIds: draft?.library_id ? [draft.library_id] : [],
    mainParentId: draft?.main_parent_id ?? "",
    capabilityIds: [],
    tieInNote: draft?.tie_in_note ?? "",
    noTieIn: false,
    drawnOn: draft?.drawn_on ?? [],
  }),
  fromElement: (row) => {
    const c = row.content;
    const note = str(c.tie_in_note);
    return {
      libraryIds: ids(c.subset_ids),
      mainParentId: str(c.main_parent_id),
      capabilityIds: ids(c.capability_ids),
      tieInNote: note,
      noTieIn: !note,
      drawnOn: [],
    };
  },
  validate: (v) => {
    const errors: Errors = {};
    if (!v.noTieIn && !v.tieInNote.trim()) errors.tieInNote = "Write the tie-in note, or tick that nothing may end here.";
    return errors;
  },
  toContent: (v, base) => ({
    ...base,
    main_parent_id: v.mainParentId && v.mainParentId !== NONE ? v.mainParentId : null,
    subset_ids: v.libraryIds,
    capability_ids: v.capabilityIds,
    shared_field_ids: base ? ids(base.shared_field_ids) : [],
    tie_in_note: v.noTieIn ? null : opt(v.tieInNote),
    linkml_extra: base?.linkml_extra ?? {},
  }),
  drawnOn: (v) => v.drawnOn,
  Fields: ComponentKindFields,
});

// ------------------------------------------------------------------------------------------------ component

type ComponentValues = { itemId: string; componentKindId: string; maxCount: string; ordered: boolean; sitsInId: string };

export const componentSpec = defineSpec<ComponentValues>({
  kind: "component",
  title: "Component",
  blurb: "A thing inside an item, with its own data and a role name.",
  nameLabel: "Role name of the component",
  nameHint: "The role the component plays in its item.",
  definitionHint: "What this component does in the item.",
  empty: () => ({ itemId: "", componentKindId: "", maxCount: "", ordered: false, sitsInId: "" }),
  fromElement: (row) => {
    const c = row.content;
    return {
      itemId: str(c.item_id),
      componentKindId: str(c.component_kind_id),
      maxCount: c.max_count == null ? "" : String(c.max_count),
      ordered: bool(c.ordered),
      sitsInId: str(c.sits_in_component_id),
    };
  },
  validate: (v) => {
    const errors: Errors = {};
    if (!v.itemId) errors.itemId = "Choose the item it belongs to.";
    if (!v.componentKindId) errors.componentKindId = "Choose its component kind.";
    if (v.maxCount.trim() && !isWholeNumber(v.maxCount)) errors.maxCount = "Write a whole number of 1 or more, or leave it empty for any number.";
    return errors;
  },
  toContent: (v, base) => ({
    ...base,
    item_id: v.itemId,
    component_kind_id: v.componentKindId,
    max_count: v.maxCount.trim() ? Number(v.maxCount) : null,
    ordered: v.ordered,
    limit_origin: base?.limit_origin === "dexpi" ? "dexpi" : "our_team",
    sits_in_component_id: v.sitsInId || null,
    linkml_extra: base?.linkml_extra ?? {},
  }),
  Fields: ({ values, set, errors }) => (
    <>
      <ElementPicker label="Item it belongs to" required kinds={["item"]} value={values.itemId} error={errors.itemId} onChange={(itemId) => set({ itemId })} />
      <ElementPicker
        label="Its component kind"
        required
        kinds={["component_kind"]}
        value={values.componentKindId}
        error={errors.componentKindId}
        onChange={(componentKindId) => set({ componentKindId })}
      />
      <TextInput label="How many" hint="Leave empty for any number. Write 1 for a single one." value={values.maxCount} error={errors.maxCount} onChange={(maxCount) => set({ maxCount })} />
      <Check label="They are kept in order" hint="Tick this when the order of several components matters." checked={values.ordered} onChange={(ordered) => set({ ordered })} />
      <ElementPicker
        label="Sits in another component of the item (optional)"
        kinds={["component"]}
        value={values.sitsInId}
        placeholder="It sits directly in the item"
        onChange={(sitsInId) => set({ sitsInId })}
      />
    </>
  ),
});

// ------------------------------------------------------------------------------------------------ capability

type CapabilityValues = { isLinkEnd: boolean };

export const capabilitySpec = defineSpec<CapabilityValues>({
  kind: "capability",
  title: "Capability",
  blurb: "Something an item or a component kind has or can do, shared by items from different branches.",
  nameLabel: "Name of the capability",
  nameHint: "Start the name with Holds, Has, Is or Can.",
  definitionHint: "What having this capability means.",
  empty: () => ({ isLinkEnd: false }),
  fromElement: (row) => ({ isLinkEnd: bool(row.content.is_link_end) }),
  validate: () => ({}),
  toContent: (v, base) => ({
    ...base,
    is_link_end: v.isLinkEnd,
    starter_set: base ? bool(base.starter_set) : false,
    linkml_extra: base?.linkml_extra ?? {},
  }),
  Fields: ({ values, set }) => (
    <Check
      label="Links and Connections may end at things that have it"
      checked={values.isLinkEnd}
      onChange={(isLinkEnd) => set({ isLinkEnd })}
    />
  ),
});
